import React, {
  memo, useContext, useEffect, useRef, useState,
} from 'react';
import {
  Row, Col,
} from 'antd';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import formatcoords from 'formatcoords';

import {
  CloudIcon, RainIcon, SunIcon,
} from '../../../components/icons';
import { getPlatformWeather } from '../../../services/dataset.service';
import MapQueryTypes from '../../../services/query-types/map';
import { featureFlag } from '../../../constant';
import { brandingConfig } from '../../../config';
import PlatformNotifications from './PlatformNotifications';
import Partners from './Partners';
import { updateSeagullUser } from '../../../services/user.service';
import UserContext from '../../../contexts/UserContext';

import HtmlText from '../../../components/html-text';
import '../styles.scss';
import { videoOrImage } from '../../../utils/webcam';

const PlatformInfo = (props) => {
  const {
    platform,
    isMobile,
    metadata,
    isPlatformsConsole,
  } = props;
  const shouldRenderNotifications = !isPlatformsConsole;
  const shouldRenderPartners = !isPlatformsConsole;
  const shouldRenderWeather = !isPlatformsConsole;

  const mapContainerRef = useRef(null);

  const [mapWidth, setMapWidth] = useState(600);

  useEffect(() => {
    const width = mapContainerRef.current.offsetWidth;
    if (width > 0) {
      setMapWidth(width);
    }
  }, [mapContainerRef]);

  const { seagullUser, unitPreferences, setSeagullUser } = useContext(UserContext);

  const {
    data: weatherData,
  } = useQuery(
    MapQueryTypes.REST_PLATFORM_WEATHER,
    getPlatformWeather,
    {
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    const updatedUser = {
      ...seagullUser,
      unit_preferences: unitPreferences,
    };
    updateSeagullUser(updatedUser)
      .then((data) => {
        setSeagullUser(data);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  }, [unitPreferences]);

  const getWeatherIcon = (weather) => {
    switch (weather) {
      case 'rainy':
        return <RainIcon />;
      case 'cloudy':
        return <CloudIcon />;
      case 'sunny':
        return <SunIcon />;
      default:
        break;
    }
  };

  const getDms = () => {
    if (platform?.latitude && platform?.longitude) {
      const { latitude, longitude } = platform;
      const decCoords = formatcoords(latitude, longitude).format(
        'ddX', { latLonSeparator: ', ' },
      );
      const dmsCoords = formatcoords(latitude, longitude).format(
        'DDMMssX', { latLonSeparator: ', ', decimalPlaces: 0 },
      );

      return (
        <span>
          {decCoords}
          <br />
          &#x28;
          {dmsCoords}
          &#x29;
        </span>
      );
    }

    if (platform?.deployment_site?.latitude) {
      const { latitude, longitude } = platform?.deployment_site;
      const decCoords = formatcoords(latitude, longitude).format(
        'ddX', { latLonSeparator: ', ' },
      );
      const dmsCoords = formatcoords(latitude, longitude).format(
        'DDMMssX', { latLonSeparator: ', ', decimalPlaces: 0 },
      );

      return (
        <span>
          {decCoords}
          <br />
          &#x28;
          {dmsCoords}
          &#x29;
        </span>
      );
    }

    return '';
  };

  const platformSummary = () => {
    if (platform.source_name === 'coops') {
      return (
        <HtmlText
          string={
            `${platform.platform_name} is a NOAA CO-OPS station. <br /><a target="_blank" href="https://tidesandcurrents.noaa.gov/stationhome.html?id=${platform.source_id}">View full data and information on noaa.gov</a>.`
          }
        />
      );
    }
    if (metadata) {
      const summary = metadata.find((o) => o.attribute_name === 'summary');
      if (summary) {
        return <HtmlText string={summary.attribute_value} />;
      }
    }
    return '';
  };

  const mapLat = platform?.latitude ?? platform?.deployment_site?.latitude;
  const mapLon = platform?.longitude ?? platform?.deployment_site?.longitude;

  return (
    <>
      {featureFlag.notifications && shouldRenderNotifications
        && <PlatformNotifications obsDatasetId={platform.obs_dataset_id} />}

      <div className="obs-dataset-summary">
        <div className="h3">About</div>
        <p>
          {platformSummary()}
        </p>
      </div>

      {platform?.obs_dataset_platform_assignment?.platform.webcam_url
        && (
          <div className="webcam-feed">
            {videoOrImage(
              platform?.obs_dataset_platform_assignment?.platform.webcam_url,
              false,
              isMobile,
            )}
          </div>
        )}

      <div style={{
        color: '#fff',
        borderRadius: 10,
        backgroundColor: 'rgba(255, 255, 255, .12) 40 40',
        marginTop: 21,
        overflow: 'hidden',
      }}
      >
        <div style={{ padding: 20 }}>
          <div className="h3">Location</div>
          <p>
            {platform && (
              getDms()
            )}
          </p>
        </div>

        <div
          ref={mapContainerRef}
          style={{
            height: 200,
            position: 'relative',
          }}
        >
          <Link
            to={
              mapLat && mapLon
                ? `/map?coords=${mapLon}%2C${mapLat}%2C8.6&platform=${platform?.org_platform_id}`
                : `/map?platform=${platform?.org_platform_id}`
            }
            style={{
              fontWeight: 500,
              textDecoration: 'underline',
              color: '#074A74',
              position: isMobile ? 'relative' : 'absolute',
              left: 4,
              bottom: 20,
            }}
          >
            View in Map
          </Link>
          {platform?.deployment_site?.latitude && platform?.deployment_site?.longitude && (
            <img
              alt={`${platform.platform_name} Map`}
              src={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l+074A74(${platform?.deployment_site?.longitude},${platform?.deployment_site?.latitude})/${platform?.deployment_site?.longitude},${platform?.deployment_site?.latitude},5.5,0,60/${mapWidth}x200?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`}
            />
          )}
          {platform?.latitude && platform?.longitude && (
            <img
              alt={`${platform.platform_name} Map`}
              src={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l+074A74(${platform?.longitude},${platform?.latitude})/${platform?.longitude},${platform?.latitude},5.5,0,60/${mapWidth}x200?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`}
            />
          )}
        </div>
      </div>

      {featureFlag.weather && shouldRenderWeather
        && (
          <div className="weather-container">
            <div className="title">
              <div className="h3">{platform.platform_name}</div>
              <div style={{ fontSize: 15, color: brandingConfig.colors.greyLight }}>WEATHER</div>
            </div>
            {weatherData
              && (
                <div className="top">
                  {getWeatherIcon(weatherData[1].weather)}
                  <label>{`${weatherData[1].maxTemp.value}${weatherData[1].maxTemp.unit}`}</label>
                </div>
              )}
            <div>
              {weatherData && weatherData.map((item, index) => (
                <Row className="item" key={index}>
                  <Col span={8} className="day">
                    {item.day}
                  </Col>
                  <Col span={8} className="max-temp">
                    {getWeatherIcon(item.weather)}
                    <label>{`${item.maxTemp.value}${item.maxTemp.unit}`}</label>
                  </Col>
                  <Col span={8} className="min-temp">
                    {`${item.minTemp.value}${item.minTemp.unit}`}
                  </Col>
                </Row>
              ))}
            </div>
          </div>
        )}

      {featureFlag.sponsors && shouldRenderPartners && platform.obs_dataset_partners?.length > 0
        && (
          <Partners partners={platform.obs_dataset_partners} />
        )}
    </>
  );
};

PlatformInfo.propTypes = {
  isMobile: PropTypes.bool,
  isPlatformsConsole: PropTypes.bool,
  metadata: PropTypes.array,
  platform: PropTypes.object,
};

PlatformInfo.defaultProps = {
  isMobile: false,
  isPlatformsConsole: false,
  metadata: [],
  platform: {},
};

export default memo(PlatformInfo);
